<template>
    <div ref="chartContainer">
        <svg ref="chartArea" style="min-height: 400px"></svg>
    </div>
</template>

<script>
    import * as d3 from 'd3'
    import * as _ from 'lodash'

    export default {
        name: "TimelineChart",
        props: {
            'dataseries': Array,
            'disabledSeries': Array,
            'stacked': Boolean,
            'showTotal': Boolean
        },
        data() {
            return {
                internalDisabledSeries: [],
                internalValues: [],
                actualIndex: null
            }
        },
        watch: {
            disabledSeries(newVal) {
                this.internalDisabledSeries = newVal;
                this.recalculateInternalValues();
                this.generateChart()
            },
            dataseries: {
                handler(newVal) {
                    this.recalculateInternalValues();

                    this.generateChart();
                }
            }
        },
        computed: {
            filteredSeries() {
                return this.internalValues.filter(v => !this.internalDisabledSeries.includes(v.name))
            },
            yScale() {
                return d3.scaleLinear()
                    .domain([0, d3.max(this.filteredSeries.map(val => val.values).flat(1), d => d.y)]).nice()
                    .range([this.height - this.margin.bottom, this.margin.top])
            },
            xScale() {
                return d3.scaleUtc()
                    .domain(d3.extent(this.filteredSeries.map(val => val.values).flat(1), d => new Date(d.x)))
                    .range([this.margin.left, this.width - this.margin.right])
            },
            margin() {
                return {top: 20, right: 20, bottom: 30, left: 50};
            },
            height() {
                return this.$refs.chartContainer.getBoundingClientRect().height;
            },
            width() {
                return this.$refs.chartContainer.getBoundingClientRect().width;
            }

        },
        mounted() {
            this.recalculateInternalValues();
            this.generateChart();
        },
        methods: {
            recalculateInternalValues() {
                this.internalValues = _.cloneDeep(this.dataseries);
                let interval = d3.extent(this.filteredSeries.map(val => val.values).flat(1), d => new Date(d.x));
                let date = this.$moment(interval[0]);
                if (this.stacked) {
                    while (date.isBefore(this.$moment(interval[1]))) {
                        let currentValue = 0;
                        this.filteredSeries.forEach(oneSeries => {
                            let singleValue = oneSeries.values.find(val => this.$moment(val.x).isSame(date, 'days'))
                            if (singleValue != undefined) {
                                currentValue += singleValue.y;
                                singleValue.y = currentValue;
                            }
                        })

                        date = date.add(1, 'days');
                    }
                } else if (this.showTotal) {
                    let totalSeries = {
                        name: "Total",
                        color: "black",
                        values: [],
                        dasharray: "10"
                    };
                    while (date.isBefore(this.$moment(interval[1]))) {
                        let currentValue = 0;
                        this.filteredSeries.forEach(oneSeries => {
                            let singleValue = oneSeries.values.find(val => this.$moment(val.x).isSame(date, 'days'))
                            if (singleValue != undefined) {
                                currentValue += singleValue.y;
                            }
                        })
                        totalSeries.values.push({x: date.toDate(), y: currentValue})
                        date = date.add(1, 'days');
                    }
                    this.internalValues.push(totalSeries);
                }

            },
            generateChart() {
                const svg = d3.select(this.$refs.chartArea)
                    .attr("viewBox", [0, 0, this.width, this.height])

                svg.selectAll('*').remove();

                svg.append("g")
                    .call(this.xAxis)
                // .style("opacity", 0)
                // .transition().duration(1500)
                // .style("opacity", 1)

                svg.append("g")
                    .call(this.yAxis)
                // .style("opacity", 0)
                // .transition().duration(1500)
                // .style("opacity", 1)
                ;

                let cursorGroup = svg
                    .append('g');

                let cursor = cursorGroup
                    .append('line')
                    .style("fill", "none")
                    .style("stroke-width", 1)
                    .style("stroke-dasharray", "8")
                    .attr("stroke", "#A0ACB7")
                    .attr('x1', 0)
                    .attr('x2', 0)
                    .attr('y1', 0)
                    .attr('y2', this.height - this.margin.bottom)

                let cursorRect = cursorGroup
                    .append('rect')
                    .attr('y', 0)
                    .attr('x', '-25')
                    .attr('width', '50')
                    .attr('height', '15')
                    .attr('fill', "#A0ACB7")
                    .attr('rx', 5)
                    .attr('ry', 5)

                let cursorLabel = cursorGroup
                    .append("text")
                    .text('')
                    .style('font-size', '0.75em')
                    .style("text-anchor", "middle")
                    .style('user-select', 'none')
                    .attr('y', 10)

                const path = svg.append("g")
                    .attr("id", "chartSeries")
                    .attr("fill", "none")
                    .attr("stroke-width", 1.5)
                    .attr("stroke-linejoin", "round")
                    .attr("stroke-linecap", "round")
                    .selectAll("path")
                    .attr("class", "data-series")
                    .data(this.filteredSeries)
                    .enter()
                    .append("path")
                    .attr("stroke", d => d.color)
                    .attr("stroke-dasharray", d => d.dasharray)
                    //.style("mix-blend-mode", "multiply")
                    // .attr("d", d => this.zeroLine()(d.values))
                    // .transition().duration(1000).ease(d3.easeCubic)
                    .attr("d", d => this.line()(d.values))


                svg.on('mousemove', () => {
                    let actualValue = d3.max([this.margin.left, d3.event.offsetX]);
                    let valueAsDate = this.xScale.invert(new Date(actualValue));
                    cursorGroup.attr("transform", `translate(${actualValue}, 0)`)
                    cursorLabel.text(this.$formats.formatDate(new Date(valueAsDate)))
                    // cursor.attr("x1", actualValue);
                    // cursor.attr("x2", actualValue);

                    this.handleIndex(valueAsDate);
                })

            },
            handleIndex: _.throttle(function (actualValue) {
                if (this.actualIndex != actualValue) {
                    this.actualIndex = actualValue;
                    this.$emit("indexChanged", actualValue)
                }
            }, 100),
            xAxis(g) {
                g
                    //.attr("stroke", 'red')
                    //.attr("stroke-width", 1)
                    .style("font-size", "0.75em")
                    .style("font-weight", "normal")
                    .attr("transform", `translate(0,${this.height - this.margin.bottom})`)
                    .call(d3.axisBottom(this.xScale).ticks(8).tickSizeOuter(10).tickFormat(d3.timeFormat("%d.%m.%y")))
                    .style("stroke", "A0ACB7")
                    .call(g => g.select(".domain").style("stroke", "#A0ACB7"))

            },
            yAxis(g) {
                g.attr("transform", `translate(${this.margin.left},0)`)
                    .call(d3.axisLeft(this.yScale).tickFormat(d3.format(".3s")))
                    .style("stroke", "#A0ACB7")
                    .call(g => g.select(".domain").remove())
                    .call(g => g.selectAll(".tick line").style("stroke", "#A0ACB7"))
                    .call(g => g.selectAll(".tick line").clone()
                        .attr("stroke-opacity", d => d === 1 ? null : 0.3)
                        .style("stroke-width", 0.5)
                        .style("stroke-dasharray", "4")
                        .attr("x2", this.width - this.margin.left - this.margin.right))
            },
            line() {
                return d3.line()
                    .curve(d3.curveStepBefore)
                    .defined(d => !isNaN(d.y))
                    .x((d) => this.xScale(d.x))
                    .y(d => this.yScale(d.y))
            },
            zeroLine() {
                return d3.line()
                    .curve(d3.curveStepBefore)
                    .defined(d => !isNaN(d.y))
                    .x((d) => this.xScale(d.x))
                    .y(d => this.yScale(0))
            }
        }
    }
</script>

<style scoped>

</style>